import React from 'react';
import { useField } from 'formik';
import Autocomplete from '@mui/material/Autocomplete';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import {
  Chip,
  FormHelperText,
  Grid,
  GridProps,
  InputLabel,
} from '@mui/material';
import { useResize } from 'hooks/common';
import { emailRegex } from 'constants/regex';

type SparkFormTaggedTextFieldProps = {
  name: string;
  label: string;
  placeholder?: string;
  size?: GridProps['xs'];
  sx?: TextFieldProps['sx'];
  type?: 'default' | 'email';
  readonly?: boolean;
  containerStyle?: GridProps['sx'];
};

const SparkFormTaggedTextField: React.FC<SparkFormTaggedTextFieldProps> = ({
  name,
  label,
  placeholder,
  size = 12,
  sx = {},
  type = 'default',
  readonly = false,
  containerStyle = {},
}) => {
  const [field, meta, helpers] = useField<string[]>(name);
  const { isMobile } = useResize();
  const defaultSize = size || (isMobile ? 12 : 6);
  const isError = !!meta.error;
  const handleChange = (_: React.SyntheticEvent, values: string[]) => {
    helpers.setValue(values);
  };

  const getIsTagInvalid = (tag: string) => {
    switch (type) {
      case 'default':
        return true;
      case 'email':
        return emailRegex.test(tag);
      default:
        return true;
    }
  };

  return (
    <Grid
      item
      container
      xs={defaultSize}
      direction='column'
      sx={containerStyle}
    >
      {label && <InputLabel sx={{ marginLeft: 1 }}>{label}</InputLabel>}
      <Autocomplete
        freeSolo
        multiple
        value={field.value || []}
        id={name}
        fullWidth
        options={[]}
        onChange={handleChange}
        renderTags={(value: string[], getTagProps) =>
          value.map((option: string, index: number) => {
            const invalid = getIsTagInvalid(option);

            return (
              <Chip
                label={option}
                {...getTagProps({ index })}
                sx={{
                  borderRadius: '10px',
                  bgcolor: (theme) =>
                    invalid
                      ? theme.palette.primary.light
                      : theme.palette.error.light,
                  border: (theme) =>
                    `1px solid ${
                      invalid
                        ? theme.palette.primary.main
                        : theme.palette.error.main
                    }`,
                }}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder || label || ''}
            fullWidth
            disabled={readonly}
            autoComplete='off'
            InputProps={{
              ...params.InputProps,
              readOnly: readonly,
            }}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              '& .MuiInputBase-multiline': {
                alignItems: 'flex-start',
              },
              ...sx,
            }}
          />
        )}
      />
      <FormHelperText
        sx={{
          color: (theme) => theme.palette.error.main,
          marginLeft: 1,
          marginBottom: 0.5,
        }}
      >
        {isError ? meta.error : ' '}
      </FormHelperText>
    </Grid>
  );
};

export default SparkFormTaggedTextField;
